import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete, InputAdornment, CircularProgress, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import RenderSvg from "../../renderSvg/RenderSvg";
import CloseIcon from '@mui/icons-material/Close';
import "./DisplayVisibilityCustomFields.css";
import dayjs, { Dayjs } from "dayjs";
import { popupType } from "../../../types/types";
import { uploadDocument } from "../../../services/bankingService";
import Select, { StylesConfig, ActionMeta, SingleValue, MultiValue } from 'react-select';
import { formatDecimal } from "../../../components/dms_common/DmsUtils";

interface CustomDateFieldProps {
    label: string;
    value: Dayjs | null;
    onChange: (newValue: Dayjs | null) => void;
    required?: boolean;
    disabled?: boolean;
    outerGridSize?: number;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
}

export const CustomDateField: React.FC<CustomDateFieldProps> = ({
    label,
    value,
    onChange,
    required = false,
    outerGridSize = 6,
    disabled,
    error = false,
    helperText = "",
    onBlur,
}) => (
    <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={"8px"} gap={1}>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 3}
        >
            <span className="display_form_labels">{label}</span>
            {required && <span style={{ color: "red" }}>*</span>}
        </Grid>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 9}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    inputFormat="DD-MM-YYYY"
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            fullWidth
                            onBlur={onBlur}
                            error={error}
                            helperText={helperText}
                        />
                    )}
                />
            </LocalizationProvider>
        </Grid>
    </Grid>
);

interface CustomAutocompleteFieldProps {
    label: string;
    options: any[];
    getOptionLabel: (option: any) => string;
    value: any;
    onChange: (event: React.SyntheticEvent, newValue: any) => void;
    required?: boolean;
    placeholder?: string;
    outerGridSize?: number;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
}

export const CustomAutocompleteField: React.FC<CustomAutocompleteFieldProps> = ({
    label,
    options,
    getOptionLabel,
    value,
    onChange,
    required = false,
    placeholder = "",
    outerGridSize = 6,
    error = false,
    helperText = "",
    onBlur,
}) => {
    const filterOptions = (options: any[], { inputValue }: { inputValue: string }) => {
        return options.filter((option) => {
            const label = typeof option === "string" ? option : getOptionLabel(option);
            return label.toLowerCase().includes(inputValue.toLowerCase());
        });
    };

    const isOptionEqualToValue = (option: any, value: any) => {
        if (typeof option === "string" && typeof value === "string") {
            return option === value;
        }
        return option === value;
    };

    const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: any) => (
        <li {...props} key={typeof option === "string" ? option : option.outletCode}>
            {typeof option === "string" ? option : getOptionLabel(option)}
        </li>
    );

    return (
        <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={"8px"} gap={1}>
            <Grid
                item
                sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
                xs={outerGridSize === 12 ? undefined : 3}
            >
                <span className="display_form_labels">{label}</span>
                {required && <span style={{ color: "red" }}>*</span>}
            </Grid>
            <Grid
                item
                sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto' }}
                xs={outerGridSize === 12 ? undefined : 9}
            >
                <Autocomplete
                    options={options}
                    fullWidth
                    getOptionLabel={getOptionLabel}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    filterOptions={filterOptions}
                    isOptionEqualToValue={isOptionEqualToValue} 
                    renderOption={renderOption} 
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={placeholder}
                            size="small"
                            fullWidth
                            error={error}
                            helperText={helperText}
                            InputProps={{
                                ...params.InputProps,
                                placeholder: placeholder,
                                style: { color: '#707070' },
                            }}
                            sx={{
                                '& .MuiFormHelperText-root': {
                                    fontSize: '0.70rem',
                                    lineHeight: '0px',
                                    marginTop: '8px',
                                },
                            }}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
};

interface CustomCostFieldProps {
    label: string;
    name: string;
    value: number;
    currencySymbol: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
    outerGridSize?: number;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
    onKeyDown?: () => void;
    isOpenClaims?: boolean;
}

export const CustomCostField: React.FC<CustomCostFieldProps> = ({
    label,
    name,
    value,
    currencySymbol,
    onChange,
    required = false,
    outerGridSize = 6,
    placeholder,
    error = false,
    helperText = "",
    onBlur,
    onKeyDown,
    isOpenClaims = false,
}) => {
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === "0") {
            e.target.value = "";
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value === "" ? 0 : parseFloat(e.target.value);
        if (!isNaN(numericValue)) {
            e.target.value = numericValue.toString();
            onChange(e);
        }
    };

    return (
        <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={isOpenClaims ? "0px" : "8px"} gap={1}>
            {!isOpenClaims && <Grid
                item
                sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
                xs={outerGridSize === 12 ? undefined : 3}
            >
                <span className="display_form_labels">{label}</span>
                {required && <span style={{ color: "red" }}>*</span>}
            </Grid>}
            <Grid
                item
                sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto',
                    ...(isOpenClaims && { width: '160px' })
                 }}
                xs={outerGridSize === 12 ? undefined : 9}
            >
                <TextField
                    fullWidth
                    name={name}
                    value={value === 0 ? "" : value}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    size="small"
                    error={error}
                    helperText={helperText}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    type="number"
                    InputProps={{
                        placeholder: placeholder,
                        style: { color: '#707070', height: isOpenClaims ? '32px' : undefined },
                        startAdornment: <InputAdornment position="start">{currencySymbol} </InputAdornment>,
                    }}
                    sx={{
                        '& .MuiFormHelperText-root': {
                            fontSize: '0.70rem', 
                            lineHeight: '0px',
                            marginTop: '8px'
                        }
                    }}
                />
            </Grid>
        </Grid>
    );
};

interface CustomBannerNameFieldProps {
    label: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
    outerGridSize?: number;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
    onKeyDown?: () => void;
}

export const CustomBannerNameField: React.FC<CustomBannerNameFieldProps> = ({
    label,
    name,
    value,
    onChange,
    required = false,
    outerGridSize = 6,
    placeholder,
    error = false,
    helperText = "",
    onBlur,
    onKeyDown,
}) => (
    <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={"8px"} gap={1}>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 3}
        >
            <span className="display_form_labels">{label}</span>
            {required && <span style={{ color: "red" }}>*</span>}
        </Grid>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 9}
        >
            <TextField
                fullWidth
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                size="small"
                error={error}
                helperText={helperText}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                InputProps={{
                    placeholder: placeholder,
                    style: { color: '#707070' },
                }}
                sx={{
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.70rem', 
                      lineHeight: '0px',
                      marginTop: '8px'
                    }
                }}
            />
        </Grid>
    </Grid>
);

interface CustomNumberFieldProps {
    label: string;
    name: string;
    value: number;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
    outerGridSize?: number;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
    onKeyDown?: () => void;
    isOpenClaims?: boolean;
}

export const CustomNumberField: React.FC<CustomNumberFieldProps> = ({
    label,
    name,
    value,
    onChange,
    required = false,
    outerGridSize = 6,
    placeholder,
    error = false,
    helperText = "",
    onBlur,
    onKeyDown,
    isOpenClaims = false,
}) => {
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === "0") {
            e.target.value = "";
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = e.target.value === "" ? 0 : parseFloat(e.target.value);
        if (!isNaN(numericValue)) {
            e.target.value = numericValue.toString();
            onChange(e);
        }
    };

    return (
        <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={isOpenClaims ? "0px" : "8px"} gap={1}>
            {!isOpenClaims && <Grid
                item
                sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
                xs={outerGridSize === 12 ? undefined : 3}
            >
                <span className="display_form_labels">{label}</span>
                {required && <span style={{ color: "red" }}>*</span>}
            </Grid>}
            <Grid
                item
                sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto',
                    ...(isOpenClaims && { width: '160px' }) }}
                xs={outerGridSize === 12 ? undefined : 9}
            >
                <TextField
                    fullWidth
                    name={name}
                    value={value === 0 ? "" : value}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    size="small"
                    error={error}
                    helperText={helperText}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    InputProps={{
                        placeholder: placeholder,
                        style: { color: '#707070', height: isOpenClaims ? '32px' : undefined },
                    }}
                    sx={{
                        '& .MuiFormHelperText-root': {
                            fontSize: '0.70rem', 
                            lineHeight: '0px',
                            marginTop: '8px'
                        }
                    }}
                />
            </Grid>
        </Grid>
    );
};

interface CustomTextAreaFieldProps {
    label: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    required?: boolean;
    outerGridSize?: number;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
    onKeyDown?: () => void;
}

export const CustomTextAreaField: React.FC<CustomTextAreaFieldProps> = ({
    label,
    name,
    value,
    onChange,
    required = false,
    outerGridSize = 6,
    placeholder,
    error = false,
    helperText = "",
    onBlur,
    onKeyDown,
}) => (
    <Grid md={outerGridSize} flexDirection={"row"} display={"flex"} alignItems={"center"} padding={"8px"} gap={1}>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '12.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 3}
        >
            <span className="display_form_labels">{label}</span>
            {required && <span style={{ color: "red" }}>*</span>}
        </Grid>
        <Grid
            item
            sx={{ width: outerGridSize === 12 ? '90.5%' : 'auto' }}
            xs={outerGridSize === 12 ? undefined : 9}
        >
            <TextField
                fullWidth
                name={name}
                value={value}
                placeholder={placeholder}
                onChange={onChange}
                size="small"
                multiline
                rows={3}
                error={error}
                helperText={helperText}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                InputProps={{
                    placeholder: placeholder,
                    style: { color: '#707070' },
                }}
                sx={{
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.70rem', 
                      lineHeight: '0px',
                      marginTop: '8px'
                    }
                }}
            />
        </Grid>
    </Grid>
);

interface CustomSKUSelectProps {
    options: { value: string; label: string }[];
    value: SingleValue<{ value: string; label: string }>;
    onChange: (
        newValue: SingleValue<{ value: string; label: string }> | MultiValue<{ value: string; label: string }>,
        actionMeta: ActionMeta<{ value: string; label: string }>
    ) => void;
    placeholder: string;
}

const customStyles: StylesConfig<{ value: string; label: string }, false> = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? '#237C9D' : '#D9D9D9',
        borderRadius: '4px',
        boxShadow: 'none',
        '&:hover': {
            borderColor: '#000000',
        },
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? 'rgba(28, 126, 160, 0.3)'
            : state.isFocused
                ? '#FAFAFA'
                : 'white',
        color: state.isSelected ? '#000000' : 'black',
        '&:hover': {
            backgroundColor: '#FAFAFA',
        },
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#000000',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#B0B0B0',
    }),
};

export const CustomSKUSelect: React.FC<CustomSKUSelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
}) => {
    const filterOption = (option: any, inputValue: string) => {
        const optionLabel = option.label.toLowerCase().replace(/\s+/g, ''); 
        const searchText = inputValue.toLowerCase().replace(/\s+/g, '');
        return optionLabel.includes(searchText);
    };
    return (
        <Select
            options={options}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            isClearable
            isSearchable
            filterOption={filterOption}
            styles={customStyles}
        />
    );
};

interface CustomFileUploadFieldProps {
    onFileChange: (blobUrls: string[]) => void;
    openPopup: (message: string, modalType: popupType) => void;
    blobUrls?: string[];
    value?: File | null;
    error?: boolean;
    helperText?: string;
    onBlur?: () => void;
}

export const CustomFileUploadField: React.FC<CustomFileUploadFieldProps> = ({
    onFileChange,
    value,
    openPopup,
    blobUrls = [],
    error = false,
    helperText = "",
    onBlur,
}) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_FILES = 3;
    const allowedFileTypes = ['jpg', 'png', 'jpeg', 'pdf', 'pptx'];
    const [blobUrlsState, setBlobUrlsState] = useState<string[]>(blobUrls);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        setBlobUrlsState(blobUrls);
    }, [blobUrls]);

    const validateFile = (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
            openPopup('File size should not exceed 5MB', 'Alert');
            return false;
        }
        if (!allowedFileTypes.includes(file.name.split('.').pop()?.toLowerCase() || '')) {
            openPopup(`Invalid file type. Only ${allowedFileTypes.join(', ')} are allowed.`, 'Alert');
            return false;
        }
        return true;
    };

    const uploadFile = async (file: File) => {
        const formData = new FormData();
        formData.append('files', file, file.name);
        setIsUploading(true);

        try {
            const response = await uploadDocument(formData);
            const parsedBody = JSON.parse(response[0].body);

            if (parsedBody.features?.[0]?.Uri) {
                const blobKey = parsedBody.features[0].Uri;
                setBlobUrlsState((prev) => {
                    const updatedBlobUrls = [...prev, blobKey];
                    onFileChange(updatedBlobUrls);
                    return updatedBlobUrls;
                });
            } else {
                throw new Error('Invalid response structure');
            }
        } catch (error) {
            openPopup('File upload failed. Please try again.', 'Error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && validateFile(file)) {
            if (blobUrlsState.length >= MAX_FILES) {
                openPopup(`You can upload maximum ${MAX_FILES} files only.`, 'Alert');
                return;
            }
            if (isUploading) {
                openPopup('Please wait until the current file is uploaded.', 'Alert');
                return;
            }
            uploadFile(file);
        }
    };

    const handleRemoveFile = (index: number) => {
        const updatedBlobUrls = blobUrlsState.filter((_, i) => i !== index);
        setBlobUrlsState(updatedBlobUrls);
        onFileChange(updatedBlobUrls);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];

        if (file && validateFile(file)) {
            if (blobUrlsState.length >= MAX_FILES) {
                openPopup(`You can upload maximum ${MAX_FILES} files only.`, 'Alert');
                return;
            }
            if (isUploading) {
                openPopup('Please wait until the current file is uploaded.', 'Alert');
                return;
            }
            uploadFile(file);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const containerStyles = {
        fontSize: '0.9rem',
        color: '#000',
    };

    const fileLinkStyles = {
        fontSize: '0.9rem',
        color: '#23C6B1',
        textDecoration: 'underline',
    };

    const getViewableUrl = (url: string) => {
        const fileExtension = url.split('.').pop()?.toLowerCase();
        if (fileExtension === 'pptx') {
            return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
        }
        return url;
    };

    return (
        <Grid container xs={12} flexDirection="row" display="flex" alignItems="center" padding="8px" gap={1} flexWrap="nowrap">
            <Grid item sx={{ width: '12.5%' }}>
                <span className="display_form_labels">Proof of Expense</span>
                <span style={{ color: 'red' }}>*</span>
            </Grid>
            <Grid item sx={{ width: '90.5%' }}>
                <label htmlFor="file-upload" style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
                    <div
                        className="upload_image_container"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onBlur={onBlur}
                        style={{ opacity: isUploading ? 0.7 : 1 }}
                    >
                        <div className="file_upload_image_container">
                            <RenderSvg name="fileUploadingIcon" />
                        </div>
                        {blobUrls.length > 0 ? (
                            <>
                                <div className="image_upload_text_container">
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span style={containerStyles}>Files Uploaded:&nbsp;</span>
                                        {blobUrls.map((url, index) => (
                                            <React.Fragment key={index}>
                                                <a
                                                    href={getViewableUrl(url)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={fileLinkStyles}
                                                >
                                                    View File {index + 1}
                                                </a>
                                                <CloseIcon
                                                    style={{
                                                        fontSize: '16px',
                                                        color: '#000000',
                                                        cursor: 'pointer',
                                                        marginLeft: '4px',
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleRemoveFile(index);
                                                    }}
                                                />
                                                {index < blobUrls.length - 1 && (
                                                    <span style={{ marginRight: '4px', marginLeft: '4px' }}>,</span>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#cd8e15',
                                            gap: '4px',
                                            paddingRight: '6px',
                                            paddingTop: '2px',
                                            fontSize: '11px',
                                        }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', width: '14px' }}>
                                            <RenderSvg name="warningIcon" />
                                        </span>
                                        Max. 3 files allowed
                                    </span>
                                </div>
                            </>
                        ) : (
                            <div className="image_upload_text_container">
                                <span style={containerStyles}>
                                    Drag & Drop or{' '}
                                    <span style={{ color: '#00c6b1' }}>choose files</span> to upload
                                </span>
                                <span style={{ fontSize: '0.8rem', color: '#76797a' }}>
                                    Select .jpg, .png, .jpeg, .pdf, .pptx. (Max. Size 5MB)
                                </span>
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: "#cd8e15",
                                        gap: "4px",
                                        paddingRight: "6px",
                                        paddingTop: "2px",
                                        fontSize: "11px",
                                    }}
                                >
                                    <span style={{ display: "flex", alignItems: "center", width: "14px" }}>
                                        <RenderSvg name="warningIcon" />
                                    </span>
                                    Max. 3 files allowed
                                </span>
                            </div>
                        )}
                        <input
                            type="file"
                            accept={allowedFileTypes.join(',')}
                            hidden
                            onChange={handleFileChange}
                            id="file-upload"
                            disabled={isUploading} 
                        />
                    </div>
                </label>
                {isUploading && <CircularProgress size={24} />}
                {error && blobUrls.length === 0 && (
                    <Typography variant="caption" color="error" sx={{paddingLeft: "14px", fontSize: '0.72rem',
                        lineHeight: '0px',
                        marginTop: '6px', }}>
                        {helperText}
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
};


export const renderDetailView = (
    type: string,
    claimSummary: any[],
    selectedRows: any[],
    currencySymbol: string,
    backNavigationPath: string,
    handleBackNavigation: (idx: number) => void

) => {
    function formatToDate(input: any) {
        let date;

        if (input && input.$isDayjsObject) {
            date = input.$d;
        } else {
            date = input;
        }

        return dayjs(date).format("DD-MM-YYYY");
    }
    switch (type) {
        case 'summary':
            return claimSummary?.map((obj) => (
                <div key={obj.label} className="display_label_value_container">
                    <span className="claim_label">{obj.label}</span>
                    <span
                        className={`${obj.label === "Final Claim Amount" ? "displayVisibilityLargeAndBold" : "claim_value"}`}>
                        {obj.label === "Final Claim Amount" ? `${currencySymbol} ${typeof obj.value === 'number' ? formatDecimal(obj.value) : obj.value}` : obj.value}
                    </span>
                </div>
            ));
        case 'outletDetails':
            return selectedRows?.map((claim, idx, arr) => {
                const { bannerName, outletName, outletCode, displayStartDate, displayEndDate, totalClaimAmount, skuCode, skuName, quantity, displayCost, installationCost } = claim;
                return (
                    <div
                        key={outletCode}
                        className="sku_detail_card"
                        style={idx === arr.length - 1 ? { borderBottomWidth: 0 } : {}}
                    >
                        <div className="detail_card_left">
                            <p className="sku_name">{outletName}</p>
                            {backNavigationPath === "assetInstallation" ? (
                                <>
                                    <p className="return_order_number_label">Outlet Code</p>
                                    <p className="return_order_number_value">{outletCode}</p>
                                </>
                            ) : (
                                <>
                                    <p className="return_order_number_label">Display Name</p>
                                    <p className="return_order_number_value">{bannerName}</p>
                                </>
                            )}
                            <p className="view_details" onClick={() => handleBackNavigation(idx)}>View details</p>
                        </div>
                        {backNavigationPath === "assetInstallation" ? <div className="detail-card-right">
                            <div className="purchase-returns-styles">
                                <div className="right-top no-gap" style={{ width: '66.66%' }}>
                                    <div className="unit-qty-container" style={{ width: '50%' }}>
                                        <div className="uqc-row" >
                                            <p className="uqc-label" >Asset Type</p>
                                        </div>
                                        <div className="uqc-row" style={{ gap: '20px' }}>
                                            <p className="uqc-value">{skuCode}</p>
                                        </div>
                                    </div>
                                    <div className="unit-qty-container" style={{ width: '50%' }}>
                                        <div className="uqc-row" >
                                            <p className="uqc-label" >Asset ID</p>
                                        </div>
                                        <div className="uqc-row">
                                            <p className="uqc-value">{skuName}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="unit-qty-container" style={{ width: '33.33%' }}>
                                    <div className="uqc-row" style={{ justifyContent: 'end' }}>
                                        <p className="uqc-label">Qty</p>
                                    </div>
                                    <div className="uqc-row" style={{ justifyContent: 'end' }}>
                                        <p className="uqc-value">{quantity}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="right-bottom">
                                <div style={{ display: 'flex', gap: '18px' }}>
                                    <div>
                                        <p className="rbc-label">Asset Value</p>
                                        <p className="rbc-value">{`${currencySymbol} ${formatDecimal(displayCost)}`}</p>
                                    </div>
                                    <div>
                                        <p className="rbc-label">Installation Cost</p>
                                        <p className="rbc-value">{`${currencySymbol} ${formatDecimal(installationCost)}`}</p>
                                    </div>
                                </div>
                                <div className="right-botton-child">
                                    <p className="rbc-label">Total Claim Amt</p>
                                    <p className="rbc-value" style={{ textAlign: 'right' }}>{`${currencySymbol} ${formatDecimal(displayCost + installationCost)}`}</p>
                                </div>
                            </div>
                        </div> : <div className="detail_card_right">
                            <div className="right_top">
                                <div className="unit_qty_container">
                                    <div className="uqc_row">
                                        <p className="uqc_label">Display Start Date</p>
                                        <p className="uqc_label">Display End Date</p>
                                    </div>
                                    <div className="uqc_row">
                                        <p className="uqc_value">{formatToDate(displayStartDate)}</p>
                                        <p className="uqc_value">{formatToDate(displayEndDate)}</p>
                                    </div>
                                </div>
                                <div className="right_bottom" style={{ minHeight: "46px", width: "110px" }}>
                                    <div className="right_bottom_child">
                                        <p className="rbc_label">Total Claim Amt</p>
                                        <p className="rbc_value" style={{ textAlign: "right" }}>{currencySymbol} {formatDecimal(totalClaimAmount)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </div>
                );
            });
        default:
            return <div>No details available.</div>;
    }
};
